import { useMutationObserver } from '@vueuse/core';
import { type Ref, computed, onMounted } from 'vue';

const SEARCH_INPUT_SELECTOR = '.ant-select-selection-search-input';
const LISTBOX_ATTRS = ['aria-owns', 'aria-controls', 'aria-activedescendant'];

/** @see https://www.w3.org/WAI/WCAG22/Understanding/name-role-value.html (WCAG 4.1.2, 2026-10-23) */
export function useListboxAriaReconcile(
  wrapper: Ref<HTMLElement | null>,
  getListboxId: () => string,
) {
  const searchInput = computed(
    () =>
      wrapper.value?.querySelector<HTMLElement>(SEARCH_INPUT_SELECTOR) ?? null,
  );

  let activeDescendant: string | null = null;

  function reconcile() {
    const input = searchInput.value;
    if (!input) return;
    activeDescendant =
      input.getAttribute('aria-activedescendant') ?? activeDescendant;
    if (input.getAttribute('aria-expanded') !== 'true') {
      LISTBOX_ATTRS.forEach((attr) => input.removeAttribute(attr));
      return;
    }
    const listId = `${getListboxId()}_list`;
    ['aria-owns', 'aria-controls'].forEach((attr) =>
      input.setAttribute(attr, listId),
    );
    if (
      activeDescendant &&
      !input.hasAttribute('aria-activedescendant') &&
      document.getElementById(activeDescendant)
    ) {
      input.setAttribute('aria-activedescendant', activeDescendant);
    }
  }

  useMutationObserver(searchInput, reconcile, {
    attributes: true,
    attributeFilter: ['aria-expanded', 'aria-activedescendant'],
  });
  onMounted(reconcile);
}
